import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Play,
    Pause,
    Volume2,
    VolumeX,
    Music,
    Star,
    Clock,
    User,
    ChevronLeft,
    ChevronRight,
    Heart,
    PlayCircle,
    Share2,
    Copy,
    ExternalLink
} from 'lucide-react';
import api from '../services/api';
import trackingService from '../services/tracking';
import { useAuth } from '../context/AuthContext';
import AccessLevelBadge from './AccessLevelBadge';
import { isGated } from '../utils/accessLabels';
import SignupInviteModal from './SignupInviteModal';
import ShareModal from './ShareModal';
import AccessRequiredModal from './AccessRequiredModal';

const optimizedCover = (url) => {
    if (!url || url.endsWith('-opt.webp')) return url;
    const dot = url.lastIndexOf('.');
    return dot !== -1 ? url.slice(0, dot) + '-opt.webp' : url;
};

const FeaturedSongs = ({ limit = 3, showHeader = true, className = "" }) => {
    const [featuredSongs, setFeaturedSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [volume, setVolume] = useState(0.7);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'audio' });
    const [shareModal, setShareModal] = useState({ isOpen: false, song: null });
    const [accessModal, setAccessModal] = useState({ isOpen: false, song: null });
    const [audioDurations, setAudioDurations] = useState({});
    const audioRef = useRef(new Audio());
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchFeaturedSongs();
        
        // Setup audio event listeners
        const audio = audioRef.current;
        audio.volume = volume;
        
        const handleEnded = () => {
            setCurrentlyPlaying(null);
            setCurrentTime(0);
        };
        const handleError = () => {
            console.error('Audio playback error');
            setCurrentlyPlaying(null);
            setCurrentTime(0);
            setError('Failed to play audio');
        };
        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };
        
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        
        return () => {
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.pause();
            audio.src = '';
            setCurrentTime(0);
            trackingService.cleanup();
        };
    }, []);

    useEffect(() => {
        const audio = audioRef.current;
        audio.volume = isMuted ? 0 : volume;
    }, [volume, isMuted]);

    useEffect(() => {
        if (featuredSongs.length <= 1) return;
        if (currentlyPlaying) return;
        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % featuredSongs.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [featuredSongs.length, currentlyPlaying]);

    const fetchFeaturedSongs = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/featured-songs/active?limit=${limit}`);
            
            if (response.data.success) {
                const songs = response.data.data;
                setFeaturedSongs(songs);
                
                // Preload metadata for all songs to get durations
                songs.forEach(song => {
                    if (song.file_url && !audioDurations[song.upload_id]) {
                        const tempAudio = new Audio();
                        tempAudio.src = song.file_url;

                        const captureDuration = () => {
                            if (Number.isFinite(tempAudio.duration) && tempAudio.duration > 0) {
                                handleLoadedMetadata(song, tempAudio);
                            } else {
                                // Some MP3s report Infinity until the browser is forced
                                // to seek, which triggers a correct 'durationchange'.
                                tempAudio.currentTime = 1e101;
                            }
                        };

                        tempAudio.addEventListener('loadedmetadata', captureDuration);
                        tempAudio.addEventListener('durationchange', captureDuration);
                        tempAudio.load();
                    }
                });
            } else {
                setError('Failed to load featured songs');
            }
        } catch (err) {
            console.error('Error fetching featured songs:', err);
            setError('Unable to load featured songs');
        } finally {
            setLoading(false);
        }
    };

    const handleLoadedMetadata = (song, audioElement) => {
        if (audioElement && Number.isFinite(audioElement.duration) && audioElement.duration > 0) {
            setAudioDurations(prev => ({
                ...prev,
                [song.upload_id]: audioElement.duration
            }));
        }
    };

    const togglePlayback = async (song) => {
        const audio = audioRef.current;
        
        if (currentlyPlaying === song.upload_id) {
            audio.pause();
            setCurrentlyPlaying(null);
            trackingService.stopPlayTracking(song.upload_id);
        } else {
            try {
                if (currentlyPlaying) {
                    audio.pause();
                    trackingService.stopPlayTracking(currentlyPlaying);
                }
                
                audio.src = song.file_url;
                setCurrentTime(0);
                await audio.play();
                setCurrentlyPlaying(song.upload_id);
                
                // Start tracking with the tracking service
                trackingService.startPlayTracking(song.upload_id, audio);
                
                // Update play count immediately for UI feedback
                setFeaturedSongs(prevSongs =>
                    prevSongs.map(s =>
                        s.upload_id === song.upload_id
                            ? { ...s, play_count: (s.play_count || 0) + 1 }
                            : s
                    )
                );
            } catch (err) {
                console.error('Error playing audio:', err);
                setError('Failed to play audio file');
            }
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (newVolume > 0) {
            setIsMuted(false);
        }
    };

    const nextSong = () => {
        if (featuredSongs.length > 1) {
            setCurrentIndex((prev) => (prev + 1) % featuredSongs.length);
        }
    };

    const prevSong = () => {
        if (featuredSongs.length > 1) {
            setCurrentIndex((prev) => (prev - 1 + featuredSongs.length) % featuredSongs.length);
        }
    };

    const formatDuration = (seconds) => {
        // Handle various input formats
        if (!seconds && seconds !== 0) return '0:00';
        
        let totalSeconds;
        if (typeof seconds === 'string') {
            // Handle MM:SS format
            if (seconds.includes(':')) {
                const [mins, secs] = seconds.split(':').map(Number);
                totalSeconds = (mins * 60) + secs;
            } else {
                totalSeconds = parseInt(seconds);
            }
        } else {
            totalSeconds = Math.floor(Number(seconds));
        }
        
        if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
        
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Format numbers for display (e.g., 1000 -> 1K, 1500 -> 1.5K)
    const formatNumber = (num) => {
        const number = Number(num);
        if (!number || number === 0 || isNaN(number)) return '0';
        if (number < 1000) return number.toString();
        if (number < 1000000) return `${(number / 1000).toFixed(number % 1000 === 0 ? 0 : 1)}K`;
        return `${(number / 1000000).toFixed(1)}M`;
    };

    // Share functionality
    const handleShare = (song) => {
        setShareModal({ isOpen: true, song });
    };

    // Like functionality
    const handleLike = async (song) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'audio' });
            return;
        }

        try {
            const response = await api.post(`/upload/${song.upload_id}/like`);
            
            if (response.data.success) {
                setFeaturedSongs(prev => prev.map(s => 
                    s.upload_id === song.upload_id 
                        ? { ...s, like_count: response.data.data.likeCount, user_has_liked: response.data.data.liked }
                        : s
                ));
            }
        } catch (err) {
            console.error('Error toggling like:', err);
        }
    };

    if (loading) {
        return (
            <div className={`featured-songs-widget ${className}`}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Star className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">New Releases</h2>
                    </div>
                )}

                <div className="space-y-4 animate-pulse">
                    {/* Mirrors the aspect-square main image block in the loaded layout */}
                    <div className="bg-gray-800/30 rounded-lg border border-gray-700/50 overflow-hidden">
                        <div className="aspect-square bg-gray-700 w-full"></div>
                    </div>
                    {/* Mirrors the compact secondary song rows */}
                    {limit > 1 && [...Array(limit - 1)].map((_, i) => (
                        <div key={i} className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/50">
                            <div className="flex gap-3 items-center">
                                <div className="w-12 h-12 bg-gray-700 rounded-lg flex-shrink-0"></div>
                                <div className="flex-1">
                                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`featured-songs-widget ${className}`} style={{ minHeight: '300px' }}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Star className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">New Releases</h2>
                    </div>
                )}

                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎵</div>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (featuredSongs.length === 0) {
        return (
            <div className={`featured-songs-widget ${className}`} style={{ minHeight: '300px' }}>
                {showHeader && (
                    <div className="flex items-center gap-3 mb-6">
                        <Star className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">New Releases</h2>
                    </div>
                )}

                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🎵</div>
                    <p className="text-gray-400 mb-4">No featured songs available</p>
                    <p className="text-sm text-gray-500">Check back soon for new featured music!</p>
                </div>
            </div>
        );
    }

    const currentSong = featuredSongs[currentIndex];


    return (
        <div className={`featured-songs-widget ${className} w-full max-w-full`}>
            {showHeader && (
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <Star className="w-6 h-6 text-yellow-400" />
                        <h2 className="text-xl font-bold text-white">New Releases</h2>
                    </div>
                    {featuredSongs.length > 1 && (
                        <div className="flex items-center gap-2 self-center sm:self-auto">
                            <button
                                onClick={prevSong}
                                aria-label="Previous song"
                                className="p-2 text-gray-400 hover:text-yellow-400 transition-colors touch-manipulation"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-sm text-gray-400 px-2">
                                {currentIndex + 1} / {featuredSongs.length}
                            </span>
                            <button
                                onClick={nextSong}
                                aria-label="Next song"
                                className="p-2 text-gray-400 hover:text-yellow-400 transition-colors touch-manipulation"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            <div className="space-y-4 w-full max-w-full">
                {/* Main Featured Song */}
                <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 rounded-lg border border-yellow-500/30 w-full max-w-full">
                    {/* Square Container with Image and Overlayed Content */}
                    <div className="relative w-full aspect-square group max-w-full">
                        {/* Background Image */}
                        {currentSong.cover_art_url ? (
                            <img
                                src={optimizedCover(currentSong.cover_art_url)}
                                onError={(e) => { e.target.onerror = null; e.target.src = currentSong.cover_art_url; }}
                                alt={currentSong.title}
                                className="w-full h-full object-cover object-center"
                                onContextMenu={(e) => e.preventDefault()}
                            />
                        ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                <Music className="w-16 h-16 sm:w-20 sm:h-20 text-white" />
                            </div>
                        )}
                        
                        
                        {/* Play Button (opens login/support modal when gated) */}
                        <button
                            onClick={() => {
                                if (isGated(currentSong.access_level, isAuthenticated, hasActiveSubscription)) {
                                    setAccessModal({ isOpen: true, song: currentSong });
                                } else {
                                    togglePlayback(currentSong);
                                }
                            }}
                            aria-label={currentlyPlaying === currentSong.upload_id ? 'Pause' : 'Play'}
                            className="absolute inset-0 bg-black/20 hover:bg-black/40 active:bg-black/50 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 sm:opacity-100 touch-manipulation"
                        >
                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 sm:p-4">
                                {currentlyPlaying === currentSong.upload_id ? (
                                    <Pause className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                                ) : (
                                    <Play className="w-8 h-8 sm:w-10 sm:h-10 text-white ml-1" />
                                )}
                            </div>
                        </button>
                        <AccessLevelBadge accessLevel={currentSong.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />

                        {/* Song Info Overlay - Bottom */}
                        <div className="absolute bottom-0 left-0 right-0 p-3 w-full max-w-full">
                            <div className="flex items-center justify-between gap-2 mb-2 w-full max-w-full">
                                <div className="inline-block min-w-0 max-w-full bg-black/70 rounded-lg px-3 py-1.5">
                                    <h3 className="text-base font-bold text-white truncate">
                                        {currentSong.title}
                                    </h3>
                                    <p className="text-gray-300 text-sm truncate">
                                        {currentSong.artist_name || currentSong.artist}
                                    </p>
                                </div>
                                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30 flex-shrink-0">
                                    Featured
                                </span>
                            </div>

                            <div className="flex items-center justify-between gap-3 text-xs w-full max-w-full">
                                <div className="flex items-center gap-2 min-w-0 max-w-full bg-black/70 rounded-lg px-2.5 py-1.5">
                                    <span className="flex items-center gap-1 text-gray-300">
                                        <PlayCircle className="w-3 h-3 text-blue-400" />
                                        <span className="font-medium">{currentSong.play_count || 0}</span>
                                    </span>
                                    <button
                                        onClick={() => handleLike(currentSong)}
                                        aria-label={currentSong.user_has_liked ? 'Unlike' : 'Like'}
                                        className="flex items-center text-gray-300 hover:text-red-400 transition-colors cursor-pointer"
                                    >
                                        <Heart className={`w-3 h-3 ${currentSong.user_has_liked ? 'text-red-400 fill-red-400' : 'text-red-400'}`} />
                                    </button>
                                    <span className="text-gray-400">
                                        {currentlyPlaying === currentSong.upload_id ?
                                            `${formatDuration(currentTime)} / ${formatDuration(audioDurations[currentSong.upload_id] || currentSong.duration)}` :
                                            formatDuration(audioDurations[currentSong.upload_id] || currentSong.duration)
                                        }
                                    </span>
                                </div>

                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Link
                                        to={`/view/audio/${currentSong.artist_username}/${currentSong.stored_filename || currentSong.filename}`}
                                        className="p-1.5 bg-black/70 hover:bg-black/90 text-gray-300 hover:text-yellow-400 transition-colors touch-manipulation rounded-lg"
                                        title="View track details"
                                    >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </Link>
                                    <button
                                        onClick={() => handleShare(currentSong)}
                                        aria-label="Share track"
                                        className="p-1.5 bg-black/70 hover:bg-black/90 text-gray-300 hover:text-yellow-400 transition-colors touch-manipulation rounded-lg"
                                    >
                                        <Share2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (isGated(currentSong.access_level, isAuthenticated, hasActiveSubscription)) {
                                                setAccessModal({ isOpen: true, song: currentSong });
                                            } else {
                                                togglePlayback(currentSong);
                                            }
                                        }}
                                        className="flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-bold bg-yellow-600 text-black hover:bg-yellow-500 transition-all touch-manipulation"
                                    >
                                        {currentlyPlaying === currentSong.upload_id ? (
                                            <>
                                                <Pause className="w-3 h-3" />
                                                Playing
                                            </>
                                        ) : (
                                            <>
                                                <Play className="w-3 h-3" />
                                                Play
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Featured Songs (if showing more than 1) */}
                {featuredSongs.length > 1 && limit > 1 && (
                    <div className="space-y-2">
                        {featuredSongs.slice(0, limit).filter((song, index) => index !== currentIndex).map((song, index) => (
                            <div
                                key={song.id}
                                className="bg-gray-800/30 rounded-lg p-3 sm:p-4 border border-gray-700/50 hover:border-yellow-500/30 active:border-yellow-500/50 transition-all cursor-pointer touch-manipulation"
                                onClick={() => setCurrentIndex(featuredSongs.indexOf(song))}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="relative flex-shrink-0 overflow-hidden rounded-lg">
                                        {song.cover_art_url ? (
                                            <img
                                                src={optimizedCover(song.cover_art_url)}
                                                onError={(e) => { e.target.onerror = null; e.target.src = song.cover_art_url; }}
                                                alt={song.title}
                                                width="48"
                                                height="48"
                                                className="w-12 h-12 object-cover"
                                                onContextMenu={(e) => e.preventDefault()}
                                            />
                                        ) : (
                                            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                                                <Music className="w-6 h-6 text-white" />
                                            </div>
                                        )}
                                        {isGated(song.access_level, isAuthenticated, hasActiveSubscription) && (
                                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                                <AccessLevelBadge accessLevel={song.access_level} iconOnly isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-white font-medium truncate">
                                            {song.title}
                                        </h4>
                                        <p className="text-gray-400 text-sm truncate">
                                            {song.artist_name || song.artist}
                                        </p>
                                    </div>
                                    
                                    <div className="text-xs text-gray-500 flex-shrink-0">
                                        {formatDuration(audioDurations[song.upload_id] || song.duration)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Link to full gallery/music page */}
            <div className="mt-4 text-center">
                <Link
                    to="/feed/artist/TheDeepWave/audio"
                    className="inline-flex items-center gap-2 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                    Explore All Music
                    <ChevronRight className="w-4 h-4" />
                </Link>
            </div>
            
            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'audio' })}
                contentType={signupInvite.contentType}
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, song: null })}
                accessLevel={accessModal.song?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.song ? `/view/audio/${accessModal.song.artist_username}/${accessModal.song.stored_filename || accessModal.song.filename}` : null}
            />

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, song: null })}
                mediaTitle={shareModal.song?.title || 'Featured Song'}
                mediaType="audio"
                username={shareModal.song?.artist_username}
                mediaId={shareModal.song?.stored_filename || shareModal.song?.filename || shareModal.song?.upload_id}
                description={shareModal.song?.description}
                artistName={shareModal.song?.artist_name || shareModal.song?.artist}
                imageUrl={shareModal.song?.cover_art_url}
                duration={shareModal.song?.duration ? formatDuration(shareModal.song.duration) : null}
            />
        </div>
    );
};

export default FeaturedSongs;